import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/Button';
import { Field, inputStyle } from '../../components/Field';
import { apiSend, ClientApiError } from '../../api/client';
import { validateSignupInput, type FieldErrors } from '../../../shared/validation';

export function SignupScreen() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', password: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBanner('');
    const local = validateSignupInput(form);
    setErrors(local);
    if (Object.keys(local).length > 0) return;

    setBusy(true);
    try {
      await apiSend('/api/auth/signup', 'POST', form);
      navigate('/login', { replace: true, state: { justSignedUp: true } });
    } catch (caught) {
      if (caught instanceof ClientApiError && caught.fields) {
        setErrors(caught.fields);
      } else {
        setBanner(
          caught instanceof ClientApiError ? caught.message : 'Something went wrong. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create an account"
      subtitle="A Refer Now dispatcher will approve your account before you can send referrals."
      footer={
        <p>
          Already registered? <Link to="/login" style={{ color: '#fff' }}>Sign in</Link>
        </p>
      }
    >
      <form onSubmit={submit} noValidate>
        {banner && (
          <div className="error-banner" role="alert">
            {banner}
          </div>
        )}
        <Field label="Full name" htmlFor="fullName" error={errors.fullName}>
          <input
            id="fullName"
            autoComplete="name"
            style={inputStyle}
            value={form.fullName}
            onChange={(e) => update('fullName', e.target.value)}
          />
        </Field>
        <Field label="Email" htmlFor="email" error={errors.email}>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            style={inputStyle}
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
          />
        </Field>
        <Field label="Mobile number" htmlFor="phone" error={errors.phone}>
          <input
            id="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            style={inputStyle}
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
          />
        </Field>
        <Field
          label="Password"
          htmlFor="password"
          error={errors.password}
          hint="At least 12 characters."
        >
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            style={inputStyle}
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
          />
        </Field>
        <Button type="submit" full disabled={busy}>
          {busy ? 'Creating account' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
